import { visualizationApi } from "@/Api/Visualization";
import { waitForCondition } from "@/Core/Utils/MiscUtils";
import { store } from "@/Store";
import { pushToStoreList, setStoreVar } from "@/Store/StoreExternalAccess";
import { treeNodeListDeserialize } from "@/TreeNodes/Deserializers";
import { goldenLayout } from "@/UI/Layout/GoldenLayout/GoldenLayoutCommon";
import { layoutApi } from "@/Api/Layout";
import { sanitizeHtml } from "@/Core/Security/Sanitize";
import { isMobile } from "@/Core/GlobalVars";
import { replaceAllCurrentStyles, replaceAllCustomStyles, setBindingPocketStyle, } from "@/Core/Styling/StyleManager";
export const molmodaStateKeysToRetain = [
    "molecules",
    "log",
    "goldenLayout",
    "viewerVantagePoint",
    "projectTitle",
];
/**
 * Uses molmoda to parse the a molecular-model file. For molmoda-native files.
 *
 * @param  {FileInfo} fileInfo  The file to parse.
 * @returns {Promise<void | TreeNodeList>}  A promise that resolves when the file is
 *    parsed. The promise resolves to a TreeNodeList containing the frames. Can also
 *    resolve void.
 */
export async function parseUsingMolModa(fileInfo) {
    const stateFromJson = await jsonStrToState(fileInfo.contents);
    // Restore per-mol-type styles before adding molecules. addToMainTree
    // triggers updateStylesInViewer, which rebuilds each node's styles from
    // this map; restoring first keeps the loaded representations from being
    // overwritten with module defaults. Absent in older files, where this is a
    // no-op and the prior default-styling behavior is preserved.
    if (stateFromJson["currentSelsAndStyles"]) {
        replaceAllCurrentStyles(stateFromJson["currentSelsAndStyles"]);
    }
    // Update vueX store
    for (const key of molmodaStateKeysToRetain) {
        let viewer;
        let sanitizedLog = null;
        if (key === "log") {
            sanitizedLog = stateFromJson[key].map(async (logEntry) => ({
                ...logEntry,
                message: await sanitizeHtml(logEntry.message),
                parameters: await sanitizeHtml(logEntry.parameters),
            }));
            sanitizedLog = await Promise.all(sanitizedLog);
        }
        switch (key) {
            case "log":
                pushToStoreList(key, sanitizedLog);
                break;
            case "molecules":
                stateFromJson[key].addToMainTree(null, false, true, false);
                break;
            case "goldenLayout":
                if (isMobile) {
                    break;
                }
                if (stateFromJson[key]) {
                    setStoreVar("goldenLayout", stateFromJson[key]);
                    if (goldenLayout) {
                        layoutApi.setSessionLayoutActive(true); // Activate session layout mode
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        goldenLayout.loadLayout(stateFromJson[key]);
                    }
                    else {
                        console.warn("GoldenLayout object not available to load layout state.");
                    }
                }
                break;
            case "viewerVantagePoint":
                setStoreVar("viewerVantagePoint", stateFromJson[key]);
                viewer = await visualizationApi.viewer;
                // TODO: Below is hackish. Only works if 3dmoljs is the viewer.
                // That is the only option for now, but it might change in the
                // future.
                await waitForCondition(() => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    return viewer._mol3dObj !== undefined;
                });
                viewer.setView(stateFromJson[key]);
                break;
            case "projectTitle":
                setStoreVar("projectTitle", stateFromJson[key] || "");
                break;
        }
    }
    // Load custom styles if present, restoring any toggled-off state.
    if (stateFromJson["customSelsAndStyles"]) {
        replaceAllCustomStyles(stateFromJson["customSelsAndStyles"], stateFromJson["disabledCustomStyleNames"] || []);
    }
    // Restore the binding-pocket layer after molecules are present, since its
    // residue selection is computed from the loaded protein/compound set.
    // Older files omit this key, leaving the pocket inactive as before.
    if (stateFromJson["bindingPocketStyle"]) {
        setBindingPocketStyle(stateFromJson["bindingPocketStyle"]);
    }
    fixLog();
    return undefined;
}
/**
 * Sorts logs by timestamp and removes duplicates.
 */
function fixLog() {
    // Need to sort the log, make sure no duplicate keys
    let log = store.state["log"];
    // Sort by timestampSecs
    log = log.sort((a, b) => {
        const an = a.timestampSecs;
        const bn = b.timestampSecs;
        if (an < bn) {
            return -1;
        }
        if (an > bn) {
            return 1;
        }
        return 0;
    });
    // Remove duplicates. Let's consider timestampSecs + message +
    // parameters + jobId to be unique key.
    const newLog = [];
    const keysSeen = new Set([]);
    for (const logItem of log) {
        const key = [
            logItem.timestampSecs.toString(),
            logItem.message,
            logItem.parameters,
            logItem.jobId,
        ].join("-");
        if (!keysSeen.has(key)) {
            newLog.push(logItem);
            keysSeen.add(key);
        }
    }
    setStoreVar("log", newLog);
}
/**
 * Converts a json representation of the state (uncompressed molmoda file) to a
 * state object.
 *
 * @param  {string} jsonStr  The json string.
 * @returns {Promise<any>} A promise that resolves the state object.
 */
async function jsonStrToState(jsonStr) {
    // Viewer needs to reload everything, so set viewierDirty to true in all
    // cases. This is a little hackish, but easier than recursing I think.
    jsonStr = jsonStr.replace(/"viewerDirty": *false/g, '"viewerDirty":true');
    // Now convert it to an object.
    const obj = JSON.parse(jsonStr);
    /**
     * Recursively sanitizes titles in the object.
     *
     * @param {any} currentObj  The current object to sanitize.
     * @returns {Promise<void>}  A promise that resolves when the sanitization is complete
     */
    const sanitizeTitles = async function (currentObj) {
        if (!currentObj || typeof currentObj !== "object") {
            return;
        }
        if (Array.isArray(currentObj)) {
            currentObj.forEach(sanitizeTitles);
        }
        else {
            if (typeof currentObj.title === "string") {
                currentObj.title = await sanitizeHtml(currentObj.title);
            }
            if (currentObj.nodes) {
                await sanitizeTitles(currentObj.nodes);
            }
        }
    };
    if (obj.molecules) {
        await sanitizeTitles(obj.molecules);
    }
    return treeNodeListDeserialize(obj.molecules)
        .then((mols) => {
        obj.molecules = mols;
        return obj;
    })
        .catch((err) => {
        throw err;
    });
}
